import { useCurrentDate, Button, InputTextArea } from "@sellar-io/components";
import {
  OrderFulfillmentType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { canEditInvoice, validateUpsWeight } from "@sellar-io/common";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useForm, useWatch } from "react-hook-form";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { DialogCTA } from "../../../components/modal/Dialog";
import { useEvents } from "../../../hooks";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderUpdate } from "../../../hooks/api/v2/order/v2/useOrderUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { UPS_WEIGHT_CHANGE_DATE } from "../../../constants/sellarConstants.shared";
import { OrderConfirmationFormType } from "./ConfirmOrderForm";
import { ConfirmOrderModalFormFragment } from "./ConfirmOrderModalFormFragment";
import { DeliveryInstructionAlert } from "../../../components/deliveryInstructionAlert/DeliveryInstructionAlert";
import { validateFulfillmentTypeChange } from "./SellarDistributionFulfillmentChangeFormFragment/lib/validateFulfillmentTypeChange";
import { getIsSwitchingBuyerOrderFromSellarDistribution } from "./SellarDistributionFulfillmentChangeFormFragment/lib/isSwitchingBuyerOrderFromSD";

export function ModifyOrderDialogForm({
  onDismiss,
  order,
}: {
  onDismiss: () => void;
  order: OrderGetResponseType;
}): ReactElement {
  const currentDate = useCurrentDate();
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const orderUpdateMutation = useOrderUpdate(order.id);

  const form = useForm<OrderConfirmationFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      fulfillmentType: order.fulfillmentType,
      paymentDue: order.paymentDue
        ? dayjs(order.paymentDue).format("YYYY-MM-DD")
        : null,
      hasNoDraughtDutyRelief: order.hasNoDraughtDutyRelief,
      deliveryPlannedAt: order.deliveryPlannedAt
        ? dayjs(order.deliveryPlannedAt).format("YYYY-MM-DD")
        : null,
      dispatchPlannedAt: order.dispatchPlannedAt
        ? dayjs(order.dispatchPlannedAt).format("YYYY-MM-DD")
        : null,
      paymentType: order.paymentType ?? "manual",
      purchaseOrderNumber: order.purchaseOrderNumber,
      paidAt: order.paidAt,
      customInvoiceNumber: order.customInvoiceNumber,
      deliveryNumber: order.deliveryNumber,
      deliveryNotes: order.deliveryNotes,
      fulfillmentChangeReason: undefined,
      fulfillmentChangeReasonMessage: "",
      fulfillmentChangeAcknowledged: false,
    } satisfies OrderConfirmationFormType,
  });

  const { paymentType, fulfillmentType } = useWatch({
    control: form.control,
  });

  // Check if feature flag is enabled for UPS weight validation
  // if order is ups, validate weights
  const isUpsOrder = fulfillmentType === "ups";
  const upsWeightValidation = validateUpsWeight(
    order.OrderItems,
    UPS_WEIGHT_CHANGE_DATE,
    order.dispatchPlannedAt ? new Date(order.dispatchPlannedAt) : currentDate,
  );
  const shouldPreventUPSSubmission = isUpsOrder && !upsWeightValidation.isValid;

  const shouldPreventSubmission = shouldPreventUPSSubmission;
  const isFormValid = !shouldPreventSubmission;

  const onSubmit = (data: OrderConfirmationFormType) => {
    const validation = validateFulfillmentTypeChange({
      data,
      isSwitchingBuyerOrderFromSellarDistribution:
        getIsSwitchingBuyerOrderFromSellarDistribution({
          order,
          fulfillmentType: data.fulfillmentType,
        }),
    });

    if (!validation.success) {
      validation.errors.forEach(([field, error]) => {
        form.setError(field, error);
      });
      return;
    }

    if (data.fulfillmentType === "dxBasic") {
      throw new Error("Cannot update an order with fulfillment type dxBasic");
    }

    // Prevent submission if order is invalid
    if (shouldPreventSubmission) {
      return;
    }

    const canUpdateCustomInvoiceNumber =
      !order.confirmedAt ||
      (order.confirmedAt && !order.customInvoiceNumber?.length);

    const customInvoiceNumber =
      canUpdateCustomInvoiceNumber && data.customInvoiceNumber?.length
        ? data.customInvoiceNumber
        : undefined;

    const fulfillmentType =
      data.fulfillmentType === ("sellar-shipping" as OrderFulfillmentType)
        ? "courier"
        : data.fulfillmentType;

    const hasNoDraughtDutyRelief =
      order.confirmedAt || order.invoiceIssuedAt
        ? undefined
        : data.hasNoDraughtDutyRelief;

    const paymentType =
      order.confirmedAt || order.invoiceIssuedAt ? undefined : data.paymentType;

    const paidAt = !order.paidAt && data.paidAt ? data.paidAt : undefined;

    const purchaseOrderNumber = canEditInvoice(order)
      ? (data.purchaseOrderNumber ?? undefined)
      : undefined;

    const deliveryPlannedAt = data.deliveryPlannedAt
      ? data.deliveryPlannedAt
      : null;

    const dispatchPlannedAt =
      fulfillmentType === "direct" ||
      (order.confirmedAt && order.fulfillmentType === "direct")
        ? deliveryPlannedAt
        : data.dispatchPlannedAt;

    orderUpdateMutation
      .mutateAsync({
        requestingBusinessType: "supplier",
        fulfillmentType: fulfillmentType,
        ...(deliveryPlannedAt && {
          deliveryPlannedAt: dayjs(deliveryPlannedAt).toDate(),
        }),
        ...(dispatchPlannedAt && {
          dispatchPlannedAt: dayjs(dispatchPlannedAt).toDate(),
        }),
        deliveryNotes: data.deliveryNotes ?? undefined,
        deliveryNumber: data.deliveryNumber ?? undefined,
        paymentType,
        customInvoiceNumber,
        hasNoDraughtDutyRelief,
        paidAt,
        purchaseOrderNumber,
        fulfillmentChangeReason: data.fulfillmentChangeReason ?? undefined,
        fulfillmentChangeReasonMessage:
          data.fulfillmentChangeReasonMessage.trim() || undefined,
      })
      .then((orderRes) => {
        track("Order Modified", {
          ...orderRes,
          initiator: business?.Supplier ? "supplier" : "retailer",
          orderId: orderRes.id,
        });
        onDismiss();
      });
  };

  const showFulfillmentChangedAlert =
    order.fulfillmentType !== fulfillmentType && order.confirmedAt;

  return (
    <>
      {orderUpdateMutation.isError && (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(orderUpdateMutation.error)?.response?.data
              .message ?? "An unknown error occurred"
          }
        />
      )}
      {shouldPreventUPSSubmission && (
        <div className="mb-4">
          <Alert
            type="error"
            message={`The total weight of the items in this order exceeds the maximum allowed weight for UPS shipments ${
              upsWeightValidation
                ? `(${upsWeightValidation.maxAllowedWeight}kg).`
                : "."
            } Please adjust the order items or change the fulfillment type.`}
          />
        </div>
      )}
      {orderUpdateMutation.isPending ? (
        <SpinnerLegacy />
      ) : (
        <form
          className={"flex flex-col gap-2"}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {order && (
            /**
             *   TODO: refactor this fragment, needs to be more generic, shouldn't depend on prop to decide whether inside 'confirm' or 'modify' modal
             */
            <ConfirmOrderModalFormFragment
              modifyModal={true}
              order={order}
              form={form}
            />
          )}
          {paymentType === "manual" && (
            <>
              <InputLabel>Custom invoice number</InputLabel>
              <InputContainer>
                <InputText
                  {...form.register("customInvoiceNumber", {
                    disabled: !!(
                      order.confirmedAt && order.customInvoiceNumber
                    ),
                    required: false,
                  })}
                />
              </InputContainer>
            </>
          )}
          <InputLabel>Delivery number</InputLabel>
          <InputContainer>
            <InputText
              {...form.register("deliveryNumber", {
                required: false,
              })}
            />
          </InputContainer>
          <InputLabel>Delivery instructions</InputLabel>
          <InputContainer>
            <InputTextArea
              variant="legacyFixed"
              {...form.register("deliveryNotes", {
                required: false,
              })}
              required={false}
              disabled={!!order.deliveredAt || !order.acceptedAt}
              placeholder={"Delivery instructions for this order..."}
            />
          </InputContainer>
          <DeliveryInstructionAlert />
          {showFulfillmentChangedAlert && (
            <Alert
              message={""}
              title={"Order total and delivery charges are fixed"}
              type={"information"}
              className={"w-full"}
            >
              <p>
                This order has been finalised so any changes to the fulfillment
                type will not be reflected in the order total
              </p>
            </Alert>
          )}
          <DialogCTA>
            <Button type={"submit"} disabled={!isFormValid}>
              Modify
            </Button>
          </DialogCTA>
        </form>
      )}
    </>
  );
}
